/**
 * Bubbles rising through the fluid. Spawn rate scales with heat.
 *
 * Purely decorative: nothing here reads or writes game state.
 */

export interface Bubble {
  x: number;
  y: number;
  r: number;
  speed: number;
  /** Phase offset so bubbles wobble out of sync with each other. */
  phase: number;
  wobble: number;
}

export interface BubbleField {
  bubbles: Bubble[];
  /** Fractional bubbles owed since the last spawn, so low rates still work. */
  pending: number;
}

const MAX_BUBBLES = 90;

export function createBubbleField(): BubbleField {
  return { bubbles: [], pending: 0 };
}

export function clearBubbles(field: BubbleField): void {
  field.bubbles.length = 0;
  field.pending = 0;
}

/**
 * Advances the field by `dt` seconds inside a fluid column.
 * `heat` drives both how many bubbles appear and how fast they climb.
 */
export function updateBubbles(
  field: BubbleField,
  dt: number,
  heat: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  const intensity = Math.max(0, heat) / 100;

  // Idle simmer at the bottom, a rolling boil at the redline.
  const rate = 1.5 + intensity * intensity * 34;
  field.pending += rate * dt;

  while (field.pending >= 1 && field.bubbles.length < MAX_BUBBLES) {
    field.pending -= 1;
    field.bubbles.push({
      x: left + Math.random() * (right - left),
      y: bottom,
      r: 1 + Math.random() * (1.6 + intensity * 2.4),
      speed: 14 + Math.random() * 26 + intensity * 60,
      phase: Math.random() * Math.PI * 2,
      wobble: 0.6 + Math.random() * 1.6,
    });
  }
  if (field.pending > 1) field.pending = 1;

  for (let i = field.bubbles.length - 1; i >= 0; i--) {
    const b = field.bubbles[i]!;
    b.y -= b.speed * dt;
    b.phase += dt * 3.2;
    // Pop at the surface.
    if (b.y - b.r <= top) field.bubbles.splice(i, 1);
  }
}

export function drawBubbles(ctx: CanvasRenderingContext2D, field: BubbleField): void {
  ctx.save();
  for (const b of field.bubbles) {
    const x = b.x + Math.sin(b.phase) * b.wobble;
    ctx.beginPath();
    ctx.arc(x, b.y, b.r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(236, 245, 242, 0.30)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.42)';
    ctx.lineWidth = 0.7;
    ctx.stroke();
  }
  ctx.restore();
}
